import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { appleMapsUrl, googleMapsUrl, wazeUrl, telUrl } from '@shared/maps';
import { lockAppScroll, unlockAppScroll } from '@/lib/scrollLock';
import { AppleMapsIcon, GoogleMapsIcon, WazeIcon, PhoneIcon, GlobeIcon, ChevronRightIcon } from './Icons';
import type { Place } from '@shared/types';
import type { PlaceWithState } from '@/types/api';

interface ActionMenuProps {
  place: Place | PlaceWithState;
  open: boolean;
  onClose: () => void;
}

interface ActionItem {
  key: string;
  label: string;
  href: string;
  icon: ReactNode;
  external?: boolean;
  tint?: string;
}

// Bottom-sheet action menu attached to every place row. Shows the full
// set of contact / navigation / order actions available for the place,
// gated by which fields are populated. Uses a portal so it always sits
// above the app content and safely escapes any list-row's stacking
// context. Escape and backdrop-tap dismiss.
export function ActionMenu({ place, open, onClose }: ActionMenuProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      // Lock body scroll while the sheet is open on mobile.
      lockAppScroll();
      return () => {
        window.removeEventListener('keydown', onKey);
        unlockAppScroll();
      };
    }
    // Delay unmount so the closing animation can run.
    const t = window.setTimeout(() => setMounted(false), 200);
    return () => window.clearTimeout(t);
  }, [open, onClose]);

  if (!mounted && !open) return null;

  const actions: ActionItem[] = [];
  if (place.phone) {
    actions.push({
      key: 'call',
      label: `Call ${place.phone}`,
      href: telUrl(place.phone),
      icon: <PhoneIcon />,
      tint: 'var(--color-primary)',
    });
  }
  actions.push({
    key: 'gmaps',
    label: 'Google Maps',
    href: googleMapsUrl(place.address),
    icon: <GoogleMapsIcon />,
    external: true,
  });
  actions.push({
    key: 'waze',
    label: 'Waze',
    href: wazeUrl(place.latitude, place.longitude),
    icon: <WazeIcon />,
    external: true,
  });
  actions.push({
    key: 'apple',
    label: 'Apple Maps',
    href: appleMapsUrl(place.address),
    icon: <AppleMapsIcon />,
    external: true,
    tint: 'var(--color-ink-muted)',
  });
  if (place.menu_url) {
    actions.push({
      key: 'menu',
      label: 'View menu',
      href: place.menu_url,
      icon: <GlobeIcon />,
      external: true,
      tint: 'var(--color-primary)',
    });
  }
  if (place.reservation_url) {
    actions.push({
      key: 'reserve',
      label: 'Reserve',
      href: place.reservation_url,
      icon: <GlobeIcon />,
      external: true,
      tint: 'var(--color-primary)',
    });
  }
  if (place.order_url) {
    actions.push({
      key: 'order',
      label: 'Order online',
      href: place.order_url,
      icon: <GlobeIcon />,
      external: true,
      tint: 'var(--color-primary)',
    });
  }
  if (place.booking_url) {
    actions.push({
      key: 'book',
      label: 'Book',
      href: place.booking_url,
      icon: <GlobeIcon />,
      external: true,
      tint: 'var(--color-primary)',
    });
  }
  if (place.appointment_url) {
    actions.push({
      key: 'appt',
      label: 'Book appointment',
      href: place.appointment_url,
      icon: <GlobeIcon />,
      external: true,
      tint: 'var(--color-primary)',
    });
  }
  if (place.website) {
    actions.push({
      key: 'web',
      label: 'Website',
      href: place.website,
      icon: <GlobeIcon />,
      external: true,
    });
  }

  return createPortal(
    <div
      className={`action-menu ${open ? 'action-menu--open' : 'action-menu--closing'}`}
      role="dialog"
      aria-label={`Actions for ${place.name}`}
      aria-modal="true"
      onClick={(e) => {
        // Close if the backdrop itself was clicked (not the sheet content).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="action-menu__sheet" onClick={(e) => e.stopPropagation()}>
        <div className="action-menu__grabber" aria-hidden />
        <div className="action-menu__header">
          <div className="action-menu__title">{place.name}</div>
          <div className="action-menu__subtitle">{place.address}</div>
        </div>
        <ul className="action-menu__list" role="list">
          {actions.map((a) => (
            <li key={a.key}>
              <a
                className="action-menu__item"
                href={a.href}
                target={a.external ? '_blank' : undefined}
                rel={a.external ? 'noreferrer' : undefined}
                onClick={onClose}
              >
                <span className="action-menu__icon" style={a.tint ? { color: a.tint } : undefined}>
                  {a.icon}
                </span>
                <span className="action-menu__label">{a.label}</span>
                <ChevronRightIcon style={{ color: 'var(--color-ink-faint)' }} />
              </a>
            </li>
          ))}
        </ul>
        <button type="button" className="action-menu__close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
}
